// The DETERMINISTIC table-level change feed.
//
// Subscribes (lazily, once) to the manual change emitter — the feed produced by
// JS-side statement-completion emission (the actor connection's change-feed
// wrapper, and the post-pull synthetic broadcast) — and routes every delivery
// through the transaction buffer, so a multi-statement transaction lands as one
// post-COMMIT batch. On delivery it bumps the per-table write clock, then fans
// out to subscribers.
//
// This feed deliberately EXCLUDES the native update-hook feed: JS emission
// already covers every JS-initiated write, so including the hook's late,
// bridge-delayed duplicates would double-bump the clock and re-invalidate
// just-refetched queries one macrotask later. `addDatabaseChangeListener`
// bridges BOTH feeds, but a connection without the native hook (the Turso actor
// connection) never produces hook events, so its events carry only the JS
// emission's `lastInsertRowId`: a real rowid for a single-row INSERT, and some
// other row or 0 for every UPDATE, DELETE and RETURNING statement.
//
// Ordering guarantee: emission happens before the mutating statement's promise
// resolves, and the buffer flush runs before the transaction promise resolves —
// so by the time a writer's `await` continues, the clock has advanced and every
// subscriber (the root invalidator) has already scheduled its invalidations.

import { addManualDatabaseChangeListener } from "./manual-change-listener.ts";
import { deliverDatabaseChange } from "./database-change-transaction-buffer.ts";
import { bumpTableWriteClock } from "./table-write-clock.ts";

type BufferedManualChangeEvent = {
  databaseFilePath: string;
  databaseName: string;
  rowId: number;
  tableName: string;
};

type Listener = (event: BufferedManualChangeEvent) => void;

const listeners = new Set<Listener>();
let removeUpstream: (() => void) | null = null;

function ensureListening(): void {
  if (removeUpstream !== null) {
    return;
  }
  const upstream = addManualDatabaseChangeListener((event) => {
    deliverDatabaseChange((delivered) => {
      bumpTableWriteClock(delivered.tableName);
      for (const listener of listeners) {
        try {
          listener(delivered);
        } catch {
          // Listeners must not break the write path; swallow to match the
          // manual emitter's fire-and-forget semantics.
        }
      }
    }, event);
  });
  removeUpstream = () => {
    upstream.remove();
  };
}

/**
 * Subscribe to the buffered manual change feed. The clock bump for an event is
 * applied immediately before listeners run, so a listener always observes the
 * post-bump clock.
 *
 * NB: the upstream subscription (and therefore clock bumping) starts on the
 * FIRST subscribe and is never torn down — the write clock must not miss
 * commits while no app-level subscriber happens to be mounted.
 */
export function addBufferedManualDatabaseChangeListener(listener: Listener): {
  remove(): void;
} {
  ensureListening();
  listeners.add(listener);
  return {
    remove() {
      listeners.delete(listener);
    },
  };
}
